import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PublicNav } from "@/components/layout/PublicNav";
import { MobileCTABar } from "@/components/intent/MobileCTABar";
import { ExitIntentPopup } from "@/components/intent/ExitIntentPopup";
import { useIntent } from "@/context/IntentContext";

export function LandingPage() {
  return (
    <div className="brand-page min-h-dvh overflow-x-hidden">
      <PublicNav />
      <Hero />
      <ProblemSection />
      <HookSection />
      <ThreeSeasonsSection />
      <GamificationSection />
      <SocialProofSection />
      <UrgencyClose />
      <Footer />
      <MobileCTABar />
      <ExitIntentPopup />

      <style>{`
        @keyframes reveal-up {
          from { opacity: 0; transform: translateY(32px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .reveal { opacity: 0; }
        .revealed {
          animation: reveal-up 700ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
}

function useReveal<T extends HTMLElement>(delay = 0) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add("revealed"), delay);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

function PrimaryCTA({
  label,
  size = "lg",
}: {
  label: string;
  size?: "lg" | "md";
}) {
  const { open } = useIntent();
  const isLg = size === "lg";

  return (
    <button
      onClick={open}
      className={`inline-flex items-center justify-center font-semibold tracking-[0.06em] transition-all active:scale-[0.98] hover:brightness-110 ${
        isLg ? "px-8 py-4 text-[14px]" : "px-6 py-3 text-[12px]"
      }`}
      style={{
        background: "#c8913a",
        color: "#141215",
        boxShadow: "0 0 48px rgba(200,145,58,0.3), 0 4px 20px rgba(0,0,0,0.4)",
        fontFamily: "'Figtree', sans-serif",
      }}
    >
      {label}
      <svg
        className="ml-2.5 w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
        />
      </svg>
    </button>
  );
}

function Hero() {
  return (
    <section
      className="relative min-h-screen flex items-end pb-16 sm:pb-24 pt-24 overflow-hidden"
      style={{
        backgroundImage: `
          linear-gradient(to right, rgba(20,18,21,0.93) 0%, rgba(20,18,21,0.78) 45%, rgba(20,18,21,0.45) 100%),
          url('https://images.unsplash.com/photo-1576514129883-2f1d47a65da6?auto=format&fit=crop&w=1920&q=80')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center 30%",
      }}
    >

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <div className="max-w-4xl">
          <h1
            className="font-extrabold tracking-[-0.03em] leading-[0.92] mb-6"
            style={{ fontSize: "clamp(48px, 10vw, 120px)" }}
          >
            Your last Fringe season told you a story.
          </h1>
          <p
            className="font-bold tracking-[-0.02em] leading-[1.1] mb-8"
            style={{
              fontSize: "clamp(28px, 5vw, 56px)",
              color: "var(--bp-gold)",
              fontFamily: "'Archivo', sans-serif",
            }}
          >
            Do you know what it said?
          </p>
          <p
            className="leading-[1.7] max-w-xl mb-10"
            style={{
              fontSize: "clamp(15px, 1.4vw, 18px)",
              color: "var(--bp-text-dim)",
            }}
          >
            SellMoreTickets pulls your actual show sales and turns them into insights you can act on:
            which shows peaked, where your buyers came from, when they booked, and exactly what to do
            differently next time.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-5">
            <PrimaryCTA label="Get Started Free" />
            <Link
              to="/login"
              className="text-[13px] font-medium transition-colors hover:underline underline-offset-4"
              style={{ color: "var(--bp-text-faint)" }}
            >
              Already have an account? Log in
            </Link>
          </div>

          <p className="text-[13px] mb-16" style={{ color: "var(--bp-text-faint)" }}>
            Free. No credit card. Your show sales data is waiting.
          </p>

          <div
            className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-6"
            style={{ borderTop: "1px solid var(--bp-line)" }}
          >
            <p
              className="text-[11px] font-semibold tracking-[0.1em]"
              style={{ color: "var(--bp-text-faint)" }}
            >
              BUILT FOR
            </p>
            <span className="w-5 h-px" style={{ background: "var(--bp-gold)", opacity: 0.35 }} />
            <p className="text-[13px] font-medium" style={{ color: "var(--bp-text-dim)" }}>
              Adelaide Fringe
            </p>
            <span className="w-1 h-1 rounded-full" style={{ background: "var(--bp-text-faint)" }} />
            <p className="text-[13px] font-medium" style={{ color: "var(--bp-text-dim)" }}>
              Edinburgh Fringe
            </p>
            <span className="w-1 h-1 rounded-full" style={{ background: "var(--bp-text-faint)" }} />
            <p className="text-[13px]" style={{ color: "var(--bp-text-faint)" }}>
              More festivals coming
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProblemSection() {
  const ref = useReveal<HTMLDivElement>();
  const problems = [
    {
      title: "The Data Black Hole",
      body: "Your ticket sales data lives inside a portal you log into once, squint at, then close. Which shows were strong? Which pricing worked? That data holds answers you've never seen.",
    },
    {
      title: "The Guesswork Marketing",
      body: "You flyered because someone said to. You posted on Instagram because it felt like you should. You have no idea what actually moved the needle.",
    },
    {
      title: "The Missing Team",
      body: "Established acts have producers, promoters, booking agents, and publicists. You have you. That's not a disadvantage; it's a gap we fill.",
    },
  ];

  return (
    <section className="relative py-24 sm:py-36" style={{ borderTop: "1px solid var(--bp-line)" }}>
      <div ref={ref} className="reveal max-w-6xl mx-auto px-5 sm:px-8">
        <h2
          className="font-extrabold tracking-[-0.02em] leading-[1] mb-16 max-w-4xl"
          style={{ fontSize: "clamp(36px, 6vw, 72px)", textWrap: "balance" }}
        >
          You made the show.{" "}
          <span style={{ color: "var(--bp-text-dim)" }}>Nobody taught you the business.</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <div
            className="md:col-span-7 p-8 sm:p-10 transition-colors duration-300 hover:border-[var(--bp-gold)]/20"
            style={{
              background: "var(--bp-surface)",
              border: "1px solid var(--bp-line)",
            }}
          >
            <h3 className="font-bold text-xl sm:text-2xl mb-4 leading-tight">
              {problems[0].title}
            </h3>
            <p className="text-[15px] leading-[1.7]" style={{ color: "var(--bp-text-dim)" }}>
              {problems[0].body}
            </p>
          </div>

          <div className="md:col-span-5 flex flex-col gap-6 md:gap-8">
            {problems.slice(1).map((p) => (
              <div
                key={p.title}
                className="p-7 sm:p-8 flex-1 transition-colors duration-300"
                style={{
                  borderLeft: "2px solid var(--bp-gold)",
                  background: "rgba(200,145,58,0.03)",
                }}
              >
                <h3 className="font-bold text-lg mb-3 leading-tight">{p.title}</h3>
                <p className="text-[14px] leading-[1.7]" style={{ color: "var(--bp-text-dim)" }}>
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 text-center">
          <PrimaryCTA label="Get Started Free" />
        </div>
      </div>
    </section>
  );
}

function HookSection() {
  const ref = useReveal<HTMLDivElement>();
  const steps = [
    {
      text: "Tell us where you are",
      note: "upcoming season, mid-festival, or looking back at a past run. We tailor everything to you.",
    },
    {
      text: "Pick your goal",
      note: "maximise profit, fill every seat, or build your brand. Your dashboard, missions, and insights all adapt.",
    },
    {
      text: "Your Season Review builds instantly",
      note: "a complete breakdown of your Fringe run.",
    },
  ];

  const previews = [
    {
      title: "Show-by-show performance",
      body: "Which nights filled, which didn't, and why the pattern matters.",
    },
    {
      title: "Revenue breakdown",
      body: "Gross, net, fees, comps. Where your money actually went.",
    },
    {
      title: "Sales timeline",
      body: "When your tickets sold. Did early marketing work? Did you rely on last-minute walk-ups?",
    },
    {
      title: "Buyer geography",
      body: "Where your audience came from. So next time you know exactly where to target.",
    },
    {
      title: "What worked vs. what didn't",
      body: "Clear, honest analysis. Not vague advice; specific insight from your numbers.",
    },
  ];

  return (
    <section className="relative py-24 sm:py-32">
      <div ref={ref} className="reveal relative max-w-6xl mx-auto px-5 sm:px-8">
        <div
          className="p-8 sm:p-12 lg:p-16"
          style={{
            background: "var(--bp-surface)",
            border: "1px solid var(--bp-line)",
          }}
        >
          <h2
            className="font-extrabold tracking-[-0.02em] leading-[1] mb-4"
            style={{ fontSize: "clamp(32px, 5vw, 56px)" }}
          >
            Sign up today.{" "}
            <span style={{ color: "var(--bp-gold)" }}>See your last season in 5 minutes.</span>
          </h2>

          <p
            className="leading-[1.7] text-base sm:text-lg max-w-2xl mb-12"
            style={{ color: "var(--bp-text-dim)" }}
          >
            You already performed. The data already exists. We make it useful.
          </p>

          <div className="space-y-1 mb-14">
            {steps.map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-4 transition-colors duration-200 hover:bg-white/[0.03]"
                style={{ borderLeft: i === 0 ? "2px solid var(--bp-gold)" : "2px solid transparent" }}
              >
                <p className="text-sm sm:text-base leading-relaxed">
                  <span className="font-semibold">{s.text}</span>
                  <span style={{ color: "var(--bp-text-dim)" }}> - {s.note}</span>
                </p>
              </div>
            ))}
          </div>

          <p className="text-[13px] font-semibold mb-5" style={{ color: "var(--bp-gold)" }}>
            What you'll see in your Season Review
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-12">
            {previews.map((p) => (
              <div
                key={p.title}
                className="p-5 transition-colors duration-200 hover:bg-white/[0.02]"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.04)",
                }}
              >
                <p className="font-semibold text-sm mb-1.5 leading-tight">{p.title}</p>
                <p className="text-[13px] leading-[1.65]" style={{ color: "var(--bp-text-dim)" }}>
                  {p.body}
                </p>
              </div>
            ))}
          </div>

          <div
            className="p-5 sm:p-6 mb-10"
            style={{
              background: "var(--bp-gold-dim)",
              border: "1px solid rgba(200,145,58,0.18)",
            }}
          >
            <p className="text-sm sm:text-base leading-relaxed">
              <span className="font-bold" style={{ color: "var(--bp-gold-light)" }}>
                This isn't a demo.
              </span>{" "}
              It's YOUR shows, YOUR tickets, YOUR money.{" "}
              <span style={{ color: "var(--bp-text-dim)" }}>And it's free, right now.</span>
            </p>
          </div>

          <PrimaryCTA label="Get Started Free" />
        </div>
      </div>
    </section>
  );
}

const SEASON_TABS = [
  {
    key: "post",
    accent: "#eaac55",
    accentRgb: "234,172,85",
    tab: "Post-Season",
    headline: "Your season is over. Your education is just beginning.",
    body: "Most performers close their last show, pack up, and try to forget the stressful parts. The problem? Next season, they repeat every mistake.",
    body2:
      'SellMoreTickets gives you a Season Review that breaks down everything that happened, in plain language, with clear recommendations. Not a spreadsheet. A story about your season, told through data, with a chapter at the end called "How To Do Better Next Time."',
    callout:
      "Available right now. Sign up, connect your credentials, and your post-season review builds in minutes. No festival needs to be running.",
    features: [
      {
        title: "The Numbers, Decoded",
        body: "Total tickets, total revenue, attendance rates, fee breakdowns. Every number you need, none of the ones you don't.",
      },
      {
        title: "Your Best & Worst Nights",
        body: "See which performances peaked and which struggled. Spot patterns you couldn't see from the stage.",
      },
      {
        title: "Sales Curve Analysis",
        body: "Were you an early-booker show or a last-minute-walk-up show? This changes everything about how you market next time.",
      },
      {
        title: "The Optimiser",
        body: 'A concrete action plan for next season. Not "do more marketing." More like: "Start flyering 3 weeks earlier, target the CBD lunch crowd, raise your ticket price by $3."',
      },
    ],
  },
  {
    key: "pre",
    accent: "#32caab",
    accentRgb: "50,202,171",
    tab: "Pre-Season",
    headline: "The 8 weeks before opening night determine 80% of your ticket sales.",
    body: "This is where most performers lose the game, not because they're lazy, but because they don't know what to do, when to do it, or whether it's working.",
    body2:
      "SellMoreTickets gives you a week-by-week marketing roadmap, daily missions, and a readiness score that tells you exactly how prepared you are.",
    features: [
      {
        title: "Choose Your Track",
        body: "Optimising for profit, full houses, or brand reputation? Your entire dashboard, missions, and insights adapt to your goal.",
      },
      {
        title: "Marketing Planner",
        body: "A pre-built, week-by-week roadmap tailored to your show, your budget, and your available time. Not generic tips; a plan built from your data.",
      },
      {
        title: "Daily Missions",
        body: 'Small, specific, 10-15 minute tasks that build momentum. "Post your show announcement." "Email 20 people personally." Check them off and watch your readiness score climb.',
      },
      {
        title: "Launch Readiness Score",
        body: "A single number that tells you how prepared you are vs. performers who hit their targets. Gamifies preparation so the boring stuff actually feels satisfying.",
      },
      {
        title: "Predictions",
        body: "Lock in your ticket sales prediction before the season starts. We'll track it against reality. The accountability moment that makes the whole thing real.",
      },
    ],
  },
  {
    key: "in",
    accent: "#cb505f",
    accentRgb: "203,80,95",
    tab: "In-Season",
    headline: "Your phone becomes your box office, your coach, and your marketing team.",
    body: "During the festival, SellMoreTickets turns into a live command centre on your phone. Watch tickets sell in real time.",
    body2:
      "Get daily missions tailored to what's working. Receive alerts when you need to act, and confetti when something goes right.",
    features: [
      {
        title: "Live Dashboard",
        body: "Real-time ticket sales, revenue tracking, and attendance rates. Updated every 30 minutes. Glanceable from backstage.",
      },
      {
        title: "Smart Alerts",
        body: '"You just sold 5 tickets in the last hour" or "Tomorrow\'s show is at 40%. Here\'s a quick-win mission." Six smart alert types built for festival pacing.',
      },
      {
        title: "Daily Coaching Missions",
        body: "Every morning, a specific action to take today. Based on your Track goal, your sales pace, and what's worked for similar shows.",
      },
      {
        title: "Velocity Tracker",
        body: "Are you on pace to hit your target? The velocity line tells you instantly. Ahead = green. Behind = amber with a rescue plan.",
      },
      {
        title: "Post-Show Debrief",
        body: "30-second check-in after each performance. How full was the room? How was the energy? Feeds your Season Review with context.",
      },
    ],
  },
];

function ThreeSeasonsSection() {
  const ref = useReveal<HTMLDivElement>();
  const [active, setActive] = useState(0);
  const tab = SEASON_TABS[active];

  return (
    <section className="relative py-24 sm:py-36" style={{ borderTop: "1px solid var(--bp-line)" }}>
      <div ref={ref} className="reveal max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14">
          <h2
            className="font-extrabold tracking-[-0.02em] leading-[1] mb-4"
            style={{ fontSize: "clamp(36px, 6vw, 64px)" }}
          >
            One platform.{" "}
            <span style={{ color: "var(--bp-gold)" }}>Three seasons of support.</span>
          </h2>
          <p
            className="text-base sm:text-lg leading-[1.7] max-w-2xl mx-auto"
            style={{ color: "var(--bp-text-dim)" }}
          >
            SellMoreTickets coaches you through every phase of your Fringe journey: before, during,
            and after.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {SEASON_TABS.map((t, i) => (
            <button
              key={t.key}
              onClick={() => setActive(i)}
              className="px-5 sm:px-7 py-3 text-[12px] tracking-[0.1em] font-semibold transition-all"
              style={{
                background:
                  active === i ? `rgba(${t.accentRgb}, 0.12)` : "rgba(255,255,255,0.025)",
                border: `1px solid ${active === i ? t.accent : "rgba(255,255,255,0.06)"}`,
                color: active === i ? t.accent : "var(--bp-text-dim)",
                fontFamily: "'Figtree', sans-serif",
              }}
            >
              {t.tab}
            </button>
          ))}
        </div>

        <div
          key={tab.key}
          className="p-8 sm:p-12"
          style={{
            background: "var(--bp-surface)",
            border: `1px solid rgba(${tab.accentRgb}, 0.15)`,
            animation: "reveal-up 500ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <h3
            className="font-extrabold tracking-[-0.01em] leading-[1.05] mb-6 max-w-4xl"
            style={{ fontSize: "clamp(24px, 3.5vw, 40px)" }}
          >
            {tab.headline}
          </h3>

          <p
            className="text-base leading-[1.7] mb-3 max-w-3xl"
            style={{ color: "var(--bp-text-dim)" }}
          >
            {tab.body}
          </p>
          <p
            className="text-base leading-[1.7] mb-10 max-w-3xl"
            style={{ color: "var(--bp-text-dim)" }}
          >
            {tab.body2}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {tab.features.map((f) => (
              <div
                key={f.title}
                className="p-5 transition-all duration-300 hover:border-white/[0.12]"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.04)",
                }}
              >
                <p className="font-semibold text-sm mb-2 leading-tight">{f.title}</p>
                <p className="text-[13px] leading-[1.65]" style={{ color: "var(--bp-text-dim)" }}>
                  {f.body}
                </p>
              </div>
            ))}
          </div>

          {tab.callout && (
            <div
              className="p-4 sm:p-5"
              style={{
                background: `rgba(${tab.accentRgb}, 0.06)`,
                border: `1px solid rgba(${tab.accentRgb}, 0.15)`,
              }}
            >
              <p className="text-sm leading-relaxed" style={{ color: "var(--bp-text)" }}>
                {tab.callout}
              </p>
            </div>
          )}
        </div>

        <div className="mt-14 text-center">
          <p
            className="text-base sm:text-lg leading-relaxed mb-6 max-w-xl mx-auto"
            style={{ color: "var(--bp-text-dim)" }}
          >
            Your next season starts with understanding your last one.
          </p>
          <PrimaryCTA label="Get Started Free" />
        </div>
      </div>
    </section>
  );
}

function GamificationSection() {
  const ref = useReveal<HTMLDivElement>();

  return (
    <section className="relative py-24 sm:py-36" style={{ borderTop: "1px solid var(--bp-line)" }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at top, rgba(200,145,58,0.04) 0%, transparent 50%)",
        }}
      />

      <div ref={ref} className="reveal relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-14">
          <div>
            <h2
              className="font-extrabold tracking-[-0.02em] leading-[1] mb-6"
              style={{ fontSize: "clamp(32px, 5vw, 56px)" }}
            >
              Marketing your show should feel like a game.{" "}
              <span style={{ color: "var(--bp-text-dim)" }}>Not a chore.</span>
            </h2>
            <p
              className="text-base sm:text-lg leading-[1.7]"
              style={{ color: "var(--bp-text-dim)" }}
            >
              Nobody became a performer because they love flyering. SellMoreTickets wraps every
              marketing action in a system designed to keep you motivated, accountable, and having
              fun.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                title: "Daily Streaks",
                body: "Check in every day and your streak grows. Break it and you'll feel it. That's the point.",
              },
              {
                title: "XP & Levels",
                body: "Every mission, every check-in earns XP. Level up from Newcomer to Hustler to Promoter to Veteran to Legend.",
              },
              {
                title: "Badges",
                body: "Hit milestones and unlock badges: your first 100 tickets, your first 5-star review, your first sold-out show.",
              },
              {
                title: "Progress Bars",
                body: "Watch your target fill up in real time. There's nothing more satisfying than a bar that's ahead of schedule.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="p-5 transition-all duration-300 hover:border-white/[0.12]"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.04)",
                }}
              >
                <p className="font-semibold text-[15px] mb-1">{item.title}</p>
                <p className="text-[13px] leading-[1.65]" style={{ color: "var(--bp-text-dim)" }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div
          className="max-w-2xl mx-auto p-7 mb-10 text-center"
          style={{
            background: "var(--bp-gold-dim)",
            border: "1px solid rgba(200,145,58,0.15)",
          }}
        >
          <p className="text-base sm:text-lg leading-relaxed font-medium">
            Building your success as an artist and your bank account as a professional just got put
            on{" "}
            <span className="font-bold" style={{ color: "var(--bp-gold)" }}>
              turbo-drive
            </span>
            .
          </p>
        </div>

        <div className="text-center">
          <PrimaryCTA label="Get Started Free" />
        </div>
      </div>
    </section>
  );
}

function SocialProofSection() {
  const ref = useReveal<HTMLDivElement>();

  return (
    <section className="relative py-24 sm:py-36" style={{ borderTop: "1px solid var(--bp-line)" }}>
      <div ref={ref} className="reveal max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <h2
            className="font-extrabold tracking-[-0.02em] leading-[1] max-w-md lg:sticky lg:top-32"
            style={{ fontSize: "clamp(36px, 6vw, 64px)", textWrap: "balance" }}
          >
            Built by people{" "}
            <span style={{ color: "var(--bp-text-dim)" }}>who've lived it.</span>
          </h2>

          <div className="space-y-8">
            <div>
              <h3 className="font-bold text-lg mb-3 leading-tight">Lived The Fringe</h3>
              <p className="text-[14px] leading-[1.7]" style={{ color: "var(--bp-text-dim)" }}>
                Built by a team embedded in the Fringe festival world. People who've watched
                performers waste money on the wrong marketing, miss opportunities hiding in their
                own data, and fly blind when they didn't have to.
              </p>
            </div>
            <div
              className="pt-8"
              style={{ borderTop: "1px solid var(--bp-line)" }}
            >
              <h3 className="font-bold text-lg mb-3 leading-tight">Data-First, Not Opinion-First</h3>
              <p className="text-[14px] leading-[1.7]" style={{ color: "var(--bp-text-dim)" }}>
                Every recommendation comes from actual show sales, not guesswork. We analyse what
                worked for YOUR show, not what some blog said works in general.
              </p>
            </div>
            <div
              className="pt-8"
              style={{ borderTop: "1px solid var(--bp-line)" }}
            >
              <h3 className="font-bold text-lg mb-3 leading-tight">Festivals Worldwide</h3>
              <p className="text-[14px] leading-[1.7]" style={{ color: "var(--bp-text-dim)" }}>
                Adelaide Fringe. Edinburgh Festival Fringe. Melbourne International Comedy Festival.
                And more coming. If your festival uses Red61, TicketSearch, or any major ticketing
                system, we've got you covered.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function UrgencyClose() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <section
      className="relative py-28 sm:py-40 overflow-hidden"
      style={{
        borderTop: "1px solid var(--bp-line)",
        backgroundImage: `
          linear-gradient(180deg, rgba(20,18,21,0.88) 0%, rgba(20,18,21,0.72) 50%, rgba(20,18,21,0.92) 100%),
          url('https://images.unsplash.com/photo-1583795484071-3c453e3a7c71?auto=format&fit=crop&w=1920&q=80')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center 40%",
      }}
    >

      <div ref={ref} className="reveal relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <h2
          className="font-extrabold tracking-[-0.02em] leading-[0.95] mb-8"
          style={{ fontSize: "clamp(36px, 7vw, 80px)" }}
        >
          Your past season's insights are sitting there. Doing nothing.{" "}
          <span style={{ color: "var(--bp-gold)" }}>Right now.</span>
        </h2>

        <p
          className="text-base sm:text-lg leading-[1.7] mb-4 max-w-2xl mx-auto"
          style={{ color: "var(--bp-text-dim)" }}
        >
          Every insight you need to make next season better already exists inside a ticketing portal
          you haven't logged into since closing night. We'll pull it out, make sense of it, and hand
          you a plan.
        </p>

        <p className="text-base sm:text-lg leading-relaxed font-bold mb-4">
          Five minutes. That's all it takes.
        </p>

        <p
          className="text-base sm:text-lg leading-[1.7] mb-12 max-w-2xl mx-auto"
          style={{ color: "var(--bp-text-dim)" }}
        >
          And when you are planning your next festival season? Optimized results, from your show
          analysis. Better show times, venue optimization, marketing plan. You'll be starting from{" "}
          <span className="font-bold" style={{ color: "var(--bp-gold)" }}>
            insight
          </span>
          .
        </p>

        <PrimaryCTA label="Get Started Free" />

        <p className="mt-10 text-[13px]" style={{ color: "var(--bp-text-faint)" }}>
          No credit card required. Free forever for past show analytics.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-14 sm:py-16" style={{ borderTop: "1px solid var(--bp-line)" }}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 mb-12">
          <div>
            <Link to="/" className="inline-flex items-center gap-2.5 mb-4">
              <div
                className="w-7 h-7 flex items-center justify-center"
                style={{
                  background: "var(--bp-gold)",
                }}
              >
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z"
                  />
                </svg>
              </div>
              <span className="text-[14px] font-bold tracking-tight">
                SellMore<span style={{ color: "var(--bp-text-faint)" }}>Tickets</span>
              </span>
            </Link>
            <p className="text-sm max-w-md leading-relaxed" style={{ color: "var(--bp-text-dim)" }}>
              Your companion and coach in mastering Fringe show ticket sales.
            </p>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {[
              { to: "/features", label: "Features" },
              { to: "/how-it-works", label: "How It Works" },
              { to: "/login", label: "Log In" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-[12px] font-medium transition-colors hover:underline underline-offset-4"
                style={{ color: "var(--bp-text-faint)" }}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        <div
          className="pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          style={{ borderTop: "1px solid var(--bp-line)" }}
        >
          <p className="text-[11px] font-medium" style={{ color: "var(--bp-text-faint)" }}>
            Built for performers, by people who understand the Fringe.
          </p>
          <p className="text-[11px]" style={{ color: "var(--bp-text-faint)" }}>
            &copy; {new Date().getFullYear()} SellMoreTickets
          </p>
        </div>
      </div>
    </footer>
  );
}
